import React, { PureComponent, Fragment } from 'react';
import { func, bool, object, instanceOf, string } from 'prop-types';
import styled from 'styled-components';
import { createForm, formShape } from 'rc-form';
import { NameInput, DateOfBirthInput, PhoneInput, EmailInput } from './Fields';
import { fontSize, fontFamily } from '../../constants/text';
import { colors } from '../../constants/colors';
import Button from '../SolvPatternLibrary/Button';
import { HipaaConsent, PrivacyPolicy, TermsOfService } from '../LegalLinks';
import { isEmptyArray } from '../../core/util/array';
import { FIRST_NAME_ID, LAST_NAME_ID } from './Fields/ids';
import { smallerThanOrEqualTo } from '../../core/util/styledComponents';
import BrandedHeader from '../LoginForm/BrandedHeader';
import history from '../../core/history';

import { withCookies, Cookies } from 'react-cookie';
import withAuthentication from '../../core/login/withAuthentication';
import { redirectAfterLogin, shouldFinishSignup } from '../../core/login';
import Loading from '../SolvPatternLibrary/Loading';
import { isEmptyObject } from '../../core/util/object';

const DISABLE_BUTTON_TIMEOUT = 5000; // 5s in ms

const Root = styled.form`
  max-width: 325px;
  text-align: center;
  font-family: ${fontFamily.book};
  margin: 20px auto;
  -webkit-font-smoothing: antialiased;

  && p {
    font-family: ${fontFamily.book};
    color: ${colors.ladyInRed};
    margin: 4px 0;
  }

  select,
  input {
    color: ${colors.blueMonday};
  }

  label {
    font-family: ${fontFamily.book};
    color: ${colors.blueish};
  }
`;

const Header = styled.h3`
  font-size: ${fontSize.extraExtraLarge};
  font-family: ${fontFamily.bold};
  color: ${colors.blueLoadingScreen};
  margin: 0 auto;
`;

const SubHeader = styled.h4`
  font-size: ${fontSize.small};
  font-family: ${fontFamily.book};
  font-weight: normal;
  color: ${colors.blueMonday};
  margin-top: 4px;
  margin-bottom: 24px;

  ${smallerThanOrEqualTo.sm`
    margin-bottom: 12px;
  `}
`;

const Link = styled.span`
  color: ${colors.blueJayWay};
  font-family: ${fontFamily.bold};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const NameContainer = styled.div`
  display: flex;

  div:first-child {
    padding-right: 8px;
  }
`;

const BirthDateLabel = styled.span`
  display: block;
  color: ${colors.blueish};
  font-size: ${fontSize.mediumSmall};
  text-align: left;
`;

const BirthDateContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const PhoneDisclaimer = styled.div`
  font-size: ${fontSize.small};
  color: ${colors.blueish};
  text-align: left;
  margin-top: 4px;
  letter-spacing: -0.17px;
`;

const SignUpButton = styled(Button)`
  width: 100%;
  height: auto;
  padding: 10px 0;
  line-height: normal;
  box-shadow: none;
`;

const LegalShenanigans = styled.span`
  display: block;
  color: ${colors.blueish};
  font-size: ${fontSize.extraSmall};
  line-height: ${fontSize.medium};
  width: 300px;
  margin: 16px auto 0;
  letter-spacing: -0.06px;
`;

class SignUpForm extends PureComponent {
  static propTypes = {
    form: formShape.isRequired,
    submitAccount: func.isRequired,
    loginButtonClick: func.isRequired,
    isModal: bool,
    hideSignupModal: func,
    login: object,
    cookies: instanceOf(Cookies).isRequired,
    isAuthorized: bool,
    accountSummary: object,
    isLoggedIn: bool.isRequired,
    preventRedirectAfterLogin: bool,
    redirectToUrl: string,
    isBookingWidgetEmbedded: bool,
  };

  static defaultProps = {
    accountSummary: {},
  };

  state = { submitting: false, isRedirecting: false };

  componentWillUnmount() {
    clearTimeout(this.submitTimeout);
  }

  handleSubmit = e => {
    e.preventDefault();
    e.stopPropagation();

    const { form, submitAccount, isAuthorized, accountSummary } = this.props;

    submitAccount(form.getFieldsValue(), isAuthorized, accountSummary.id);

    this.setState({ submitting: true });
    this.submitTimeout = setTimeout(
      () => this.setState({ submitting: false }),
      DISABLE_BUTTON_TIMEOUT
    );
  };

  isButtonDisabled = () => {
    const { submitting } = this.state;
    if (submitting) return submitting;

    const { getFieldsValue, getFieldsError } = this.props.form;
    const values = Object.values(getFieldsValue());
    const errors = Object.values(getFieldsError());

    return isEmptyArray(values) || values.some(value => !value) || errors.some(error => error);
  };

  handleCloseModal = () => {
    const { isModal, hideSignupModal } = this.props;
    if (!isModal) {
      return history.push('/');
    }

    return hideSignupModal();
  };

  redirectAfterAccountReceived = () => {
    const {
      preventRedirectAfterLogin,
      isBookingWidgetEmbedded,
      accountSummary,
      redirectToUrl,
      isLoggedIn,
    } = this.props;

    if (shouldFinishSignup(accountSummary)) {
      return;
    }

    if (preventRedirectAfterLogin) return;

    if (isLoggedIn) {
      const isAfterSignup = true;
      this.setState({ isRedirecting: true }, () =>
        redirectAfterLogin(accountSummary, redirectToUrl, isBookingWidgetEmbedded, isAfterSignup)
      );
    }
  };

  render() {
    const { isRedirecting } = this.state;
    const { form, loginButtonClick, isModal, login, accountSummary, isAuthorized } = this.props;

    if (
      !isRedirecting &&
      !isEmptyObject(accountSummary) &&
      !isEmptyArray(accountSummary.user_profiles)
    ) {
      this.redirectAfterAccountReceived();

      return <Loading />;
    }

    if (isRedirecting) {
      return <Loading />;
    }
    return (
      <Fragment>
        {!isModal && !isAuthorized && (
          <BrandedHeader closeModal={this.handleCloseModal} loginAction={login.action} />
        )}
        <Root onSubmit={this.handleSubmit}>
          <Header>{isAuthorized ? 'Welcome to Solv' : "Sign Up—It's Free!"}</Header>
          <SubHeader>
            Already have an account? <Link onClick={loginButtonClick}>Log In</Link>
          </SubHeader>
          <NameContainer>
            <NameInput fieldId={FIRST_NAME_ID} form={form} />
            <NameInput fieldId={LAST_NAME_ID} form={form} />
          </NameContainer>
          <BirthDateLabel>DATE OF BIRTH</BirthDateLabel>
          <BirthDateContainer>
            <DateOfBirthInput form={form} />
          </BirthDateContainer>
          <PhoneInput form={form} initialValue={accountSummary.phone} />
          <PhoneDisclaimer>Your new account will be associated with this number.</PhoneDisclaimer>
          <EmailInput form={form} />
          <SignUpButton
            data-testid="sign-up-submit"
            type="submit"
            disabled={this.isButtonDisabled()}
          >
            Sign Up & Agree
          </SignUpButton>
          <LegalShenanigans>
            By clicking 'Sign Up & Agree' you agree to create an account and have read, understood,
            and agreed to Solv Health's
            <br />
            <TermsOfService />, <PrivacyPolicy />, and <HipaaConsent />.
          </LegalShenanigans>
        </Root>
      </Fragment>
    );
  }
}

export default createForm()(withCookies(withAuthentication(SignUpForm)));
